import { useEffect, useState } from "react";
import { AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

interface RateLimitBannerProps {
  retryAfterSeconds: number;
  /** Optional message from the server. */
  message?: string;
  /** Called when the countdown reaches zero. */
  onReady?: () => void;
  /** Retry the last action now (only enabled once countdown finishes). */
  onRetry?: () => void;
  /** Dismiss the banner. */
  onDismiss?: () => void;
}

/**
 * Friendly banner shown when a solve/practice endpoint returns HTTP 429.
 * Counts down the Retry-After window and keeps the retry button disabled
 * until the client is safe to try again.
 */
export function RateLimitBanner({
  retryAfterSeconds,
  message,
  onReady,
  onRetry,
  onDismiss,
}: RateLimitBannerProps) {
  const [remaining, setRemaining] = useState(retryAfterSeconds);

  useEffect(() => {
    setRemaining(retryAfterSeconds);
  }, [retryAfterSeconds]);

  useEffect(() => {
    if (remaining <= 0) {
      onReady?.();
      return;
    }
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [remaining, onReady]);

  const ready = remaining <= 0;

  return (
    <div
      role="alert"
      className="rounded-xl p-4 border bg-neon-yellow/10 border-neon-yellow/40 text-ink flex items-start gap-3"
    >
      <AlertTriangle className="size-5 shrink-0 mt-0.5 text-neon-pink" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm">Too many requests</p>
        <p className="text-xs text-ink-muted mt-0.5">
          {message ?? "You're going a bit fast. Please slow down for a moment so we can keep things smooth for everyone."}
        </p>
        <div className="flex items-center gap-3 mt-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-muted tabular-nums">
            <Clock className="size-3.5" aria-hidden="true" />
            {ready ? "Ready to retry" : `Try again in ${remaining}s`}
          </span>
          {onRetry && (
            <Button
              size="sm"
              variant="scan"
              onClick={onRetry}
              disabled={!ready}
              aria-disabled={!ready}
            >
              Retry now
            </Button>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-xs text-ink-muted hover:text-ink"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}